import { useId } from "react";
import { useTranslation } from "react-i18next";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { useConfirmationDialogContext } from "../confirmation-dialog/ConfirmationDialogContext";
import { FormikErrorNotification } from "../forms/formik-error-notification/FormikErrorNotification";
import { FormDialogProps } from "./FormDialog.types";

// Fraction of the viewport height the dialog paper is allowed to occupy. Consumers that need
// to size content to the dialog's available space (e.g. a resizable field) reference this.
export const DIALOG_MAX_HEIGHT_RATIO = 0.9;

export function FormDialog({
  title,
  onClose,
  formik,
  confirmDirtyClose = true,
  disableErrorNotification = false,
  cancelLabel,
  submitLabel,
  submitDisabled = false,
  submitColor,
  submitStartIcon,
  extraAction,
  aboveActions,
  children,
  fullWidth,
  paperSx,
}: FormDialogProps) {
  const titleId = useId();
  const formId = useId();
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();

  const handleClose = () => {
    if (formik.dirty && confirmDirtyClose) {
      showConfirmationDialog({
        dialogTitleText: t("common.formDialog.unsavedChangesTitle"),
        dialogText: t("common.formDialog.unsavedChangesText"),
        agreeText: t("common.formDialog.discardChanges"),
        discardText: t("common.cancel"),
        onClose: (confirmed) => {
          if (confirmed) {
            onClose();
          }
        },
      });
    } else {
      onClose();
    }
  };

  const handleFormSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void formik.submitForm();
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      aria-labelledby={titleId}
      fullWidth={fullWidth}
      slotProps={{
        paper: {
          sx: [
            {
              maxHeight: `${DIALOG_MAX_HEIGHT_RATIO * 100}%`,
              maxWidth: { md: "90%", lg: "1120px" },
              overflow: "hidden",
            },
            ...(paperSx !== undefined ? [paperSx] : []),
          ] as SxProps<Theme>,
        },
      }}
      scroll="paper"
    >
      {!disableErrorNotification && <FormikErrorNotification formik={formik} />}
      <DialogTitle
        id={titleId}
        variant="h1"
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {title}
        <IconButton onClick={handleClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5 }}>
        <form noValidate onSubmit={handleFormSubmit} id={formId}>
          {children}
        </form>
      </DialogContent>
      {aboveActions && <Box sx={{ px: 5, pb: 2, display: "flex", justifyContent: "flex-end" }}>{aboveActions}</Box>}
      <DialogActions
        sx={{ p: 5, pt: 0, gap: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            {cancelLabel ?? t("common.cancel")}
          </Button>
          {extraAction?.left}
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {extraAction?.right}
          {submitLabel && (
            <Button
              type="submit"
              variant="contained"
              color={submitColor}
              startIcon={submitStartIcon}
              disabled={formik.isSubmitting || submitDisabled}
              form={formId}
            >
              {submitLabel}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
